import { ReactNode, useState } from 'react'
import { useIntl } from 'react-intl'
import toast from 'react-hot-toast'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link, TextField, Typography } from '@mui/material'
import translate from '../../i18n/translate'
import { formatOtpDate } from '../UI/OtpPhonesPage'
import { getApiErrorMessage, RobotSmsDeclaration, RobotSmsReviewAction, robotSmsPolicyApi } from '../../modules/services/robotSmsPolicyService'
import DeclarationStatusChip from './DeclarationStatusChip'
import useStyles from './useStyles'

// Same rules as the API: what each status can be changed to
const AVAILABLE_ACTIONS: Record<RobotSmsDeclaration['reviewStatus'], RobotSmsReviewAction[]> = {
  pending: ['approve', 'reject', 'revoke'],
  approved: ['revoke'],
  rejected: ['approve'],
  revoked: ['approve'],
}

const BROWSER_IMAGE_TYPES = ['jpg', 'jpeg', 'png', 'webp']

const Row = ({ label, children }: { label: ReactNode, children: ReactNode }) => (
  <Box display="flex" gap={2} py={0.5}>
    <Typography color="text.secondary" minWidth={200}>{label}</Typography>
    <Typography component="div" sx={{ wordBreak: 'break-word' }}>{children || '—'}</Typography>
  </Box>
)

type Props = {
  declaration: RobotSmsDeclaration
  onClose: () => void
}

const DeclarationDetailsDialog = ({ declaration, onClose }: Props) => {
  const intl = useIntl()
  const classes = useStyles()

  // Reject / revoke need a reason, which the organizer sees
  const [actionWithReason, setActionWithReason] = useState<RobotSmsReviewAction | null>(null)
  const [comment, setComment] = useState('')

  const [review, { isLoading }] = robotSmsPolicyApi.useReviewRobotSmsDeclarationMutation()

  const runAction = async (action: RobotSmsReviewAction, reason?: string) => {
    try {
      await review({ id: declaration.id, action, comment: reason?.trim() || undefined }).unwrap()
      toast.success(intl.formatMessage({ id: `robotSmsDeclarationAction_${action}_success` }))
      setActionWithReason(null)
      setComment('')
    } catch (error) {
      toast.error(getApiErrorMessage(error) || intl.formatMessage({ id: 'robotSmsPolicyUpdateFailed' }))
    }
  }

  const account = declaration.user
  const accountName = account ? [account.name, account.surname].filter(Boolean).join(' ') : ''
  const isBrowserImage = BROWSER_IMAGE_TYPES.includes(declaration.idPhotoType || '')

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.DialogTitle}>
        {translate('robotSmsDeclarationTitle')}
        <DeclarationStatusChip status={declaration.reviewStatus} />
      </DialogTitle>

      <DialogContent dividers>
        <Row label={translate('robotSmsDeclarant')}>{declaration.declarantFullName}</Row>
        <Row label={translate('robotSmsDeclarationIdNumber')}>{declaration.declarantIdNumber}</Row>
        <Row label={translate('robotSmsDeclarationBusiness')}>{declaration.businessName}</Row>
        <Row label={translate('robotSmsDeclarationRegistrationNumber')}>{declaration.businessRegistrationNumber}</Row>
        <Row label={translate('robotSmsDeclarationDataSource')}>{declaration.dataSource}</Row>
        <Row label={translate('robotSmsDeclarationContacts')}>
          {declaration.email}
          <br />
          {declaration.phone}
        </Row>

        <Divider sx={{ my: 1.5 }} />

        <Row label={translate('robotSmsDeclarationAccount')}>
          {account && (
            <>
              {accountName || '—'} · #{account.id}
              <br />
              {[account.phone, account.email].filter(Boolean).join(' · ')}
            </>
          )}
        </Row>
        <Row label={translate('robotSmsDeclarationSignedAt')}>
          {formatOtpDate(declaration.acceptedAt)} · {translate('robotSmsPolicyVersion')} {declaration.policyVersion}
        </Row>
        <Row label="IP / User-Agent">
          {declaration.ip}
          <br />
          <Typography variant="body2" color="text.secondary">{declaration.userAgent}</Typography>
        </Row>
        <Row label={translate('robotSmsDeclarationReviewed')}>
          {declaration.reviewedAt && (
            <>
              {formatOtpDate(declaration.reviewedAt)} · {declaration.reviewedByPhone || intl.formatMessage({ id: 'robotSmsDeclarationAuto' })}
              {declaration.reviewComment && (
                <>
                  <br />
                  {intl.formatMessage({ id: 'robotSmsDeclarationReason' }, { reason: declaration.reviewComment })}
                </>
              )}
            </>
          )}
        </Row>

        <Divider sx={{ my: 1.5 }} />

        <Box className={classes.DeclarationFiles}>
          <Box className={classes.DeclarationFile}>
            <Typography fontWeight={600}>{translate('robotSmsDeclarationIdPhoto')}</Typography>
            {declaration.idPhotoUrl && isBrowserImage && (
              <a href={declaration.idPhotoUrl} target="_blank" rel="noreferrer">
                <img src={declaration.idPhotoUrl} alt="" className={classes.IdPhotoImage} />
              </a>
            )}
            {declaration.idPhotoUrl && (
              <Link href={declaration.idPhotoUrl} target="_blank" rel="noreferrer" color="#fc7f03">
                {translate('robotSmsDeclarationOpenFile')} ({declaration.idPhotoType})
              </Link>
            )}
          </Box>
          <Box className={classes.DeclarationFile}>
            <Typography fontWeight={600}>{translate('robotSmsDeclarationSignature')}</Typography>
            {declaration.signatureUrl && <img src={declaration.signatureUrl} alt="" className={classes.SignatureImage} />}
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={1}>
          {translate('robotSmsDeclarationLinksHint')}
        </Typography>

        {actionWithReason && (
          <Box mt={2}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              color="warning"
              label={intl.formatMessage({ id: 'robotSmsDeclarationReasonLabel' })}
              helperText={intl.formatMessage({ id: 'robotSmsDeclarationReasonHint' })}
              value={comment}
              inputProps={{ maxLength: 500 }}
              onChange={(e) => setComment(e.target.value)}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={actionWithReason ? () => setActionWithReason(null) : onClose} disabled={isLoading}>
          {translate(actionWithReason ? 'cancel' : 'robotSmsDeclarationClose')}
        </Button>

        {actionWithReason ? (
          <Button color="error" variant="contained" disabled={isLoading} onClick={() => runAction(actionWithReason, comment)}>
            {translate(`robotSmsDeclarationAction_${actionWithReason}`)}
          </Button>
        ) : (
          AVAILABLE_ACTIONS[declaration.reviewStatus].map((action) => (
            <Button
              key={action}
              color={action === 'approve' ? 'success' : 'error'}
              variant={action === 'approve' ? 'contained' : 'outlined'}
              disabled={isLoading}
              onClick={() => (action === 'approve' ? runAction(action) : setActionWithReason(action))}
            >
              {translate(action === 'approve' && declaration.reviewStatus === 'revoked' ? 'robotSmsDeclarationRestore' : `robotSmsDeclarationAction_${action}`)}
            </Button>
          ))
        )}
      </DialogActions>
    </Dialog>
  )
}

export default DeclarationDetailsDialog
